import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { IoMdImage } from 'react-icons/io';

import { getTransparentColor } from '@mssgme/helpers';
import { mediaUtils } from '@mssgme/shared';
import { IconButton, LandingsPropTypes, LazyImage } from '@mssgme/ui';
import { CloseIcon, MinusIcon, PlusIcon } from '@mssgme/icons';
import { usePageColors } from '../../hooks';
import { printPrice } from '../printPrice';

import styles from './OrderItem.scss';

const propTypes = {
    item: LandingsPropTypes.orderItem.isRequired,
    tag: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType]),
    onAdjust: PropTypes.func,
};

export function OrderItem({ item, onAdjust, tag: Tag = 'li' }) {
    const colors = usePageColors();
    const style = useMemo(() => ({ borderColor: getTransparentColor(colors.color, 0.2) }));
    const isEditable = !!onAdjust;
    const isMultiEditable = item.quantity > 1 && isEditable;

    const handleAdjust = useCallback(
        (delta) => onAdjust([{ productId: item.productId, quantity: Math.max(0, item.quantity + delta) }]),
        [item, onAdjust]
    );
    const handleAdd = useCallback(() => handleAdjust(+1), [handleAdjust]);
    const handleSubtract = useCallback(() => handleAdjust(-1), [handleAdjust]);
    const handleRemove = useCallback(() => handleAdjust(-Infinity), [handleAdjust]);

    return (
        <Tag className={styles.root}>
            <div className={styles.divider} style={{ backgroundColor: colors.color }} />
            <div className={styles.thumbnail}>
                {item.product.photoUrl ? (
                    <LazyImage src={mediaUtils.generateProductThumbnailUrl(item.product.photoUrl)} alt={item.name} />
                ) : (
                    <div className={styles.emptyPlaceholder}>
                        <IoMdImage size={70} color="#fff" />
                    </div>
                )}
            </div>
            <div className={styles.meta}>
                <div className={styles.title} style={style}>
                    <div>{item.name}</div>

                    {isEditable && (
                        <IconButton className={styles.knob} onClick={handleRemove}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </div>
                <div className={styles.details}>
                    <div className={styles.row}>
                        <div className={styles.quantity}>
                            {isEditable && (
                                <IconButton
                                    className={styles.knob}
                                    disabled={item.quantity <= 1}
                                    onClick={handleSubtract}
                                >
                                    <MinusIcon size={16} />
                                </IconButton>
                            )}
                            <div className={styles.quantityText} style={{ margin: isEditable ? '0 8px' : '0' }}>
                                {!isEditable && 'x'}
                                {item.quantity}
                            </div>
                            {isEditable && (
                                <IconButton className={styles.knob} onClick={handleAdd}>
                                    <PlusIcon size={16} />
                                </IconButton>
                            )}
                        </div>
                        <div className={styles.price} style={{ fontWeight: isMultiEditable ? 400 : 600 }}>
                            {isMultiEditable && `${item.quantity} x `}
                            {printPrice(isEditable ? item.price : item.subtotal)}
                        </div>
                    </div>

                    {isMultiEditable && <div className={styles.subTotal}>= {printPrice(item.subtotal)}</div>}
                </div>
            </div>
        </Tag>
    );
}

OrderItem.propTypes = propTypes;
